import { Show, createMemo, createSignal, type Component } from "solid-js";
import { PanelLayout, type PanelStatus } from "@/shared/panel-base";
import { DataTable, type Column } from "@/shared/data-table";
import { createToolQuery } from "@/shared/query";
import { useToolClient } from "@/shared/tool-client";
import { useWs } from "@/shared/ws";
import { PanelBadge } from "@/shared/panel-primitives";
import { registerPanel } from "@/panel-registry";
import { PanelChrome } from "../console/ui";

type Mode = "byProcess" | "byName";

interface CheckResult {
  available: boolean;
  version?: string | null;
  tool?: string;
  note?: string;
}

interface ProcessHandleRow {
  id: string;
  type: string;
  name: string;
}

interface ListByProcessResult {
  pid: number;
  count: number;
  handles: ProcessHandleRow[];
  stderr?: string;
}

interface FindRow {
  process: string;
  pid: number;
  type: string;
  name: string;
}

interface FindByNameResult {
  filename: string;
  limit: number;
  count: number;
  rows: FindRow[];
  stderr?: string;
}

const HandlesPanel: Component = () => {
  const ws = useWs();
  const tools = useToolClient();

  const enabled = () => ws.state.nativeAgentOnline && ws.state.supportApproved;
  const pluginVersion = () => ws.state.pluginVersions["handles"];

  const [mode, setMode] = createSignal<Mode>("byProcess");

  // Mode "byProcess" — handle.exe ne supporte que le PID. On accepte un nom
  // saisi par l'opérateur et on résout via process_list avant l'appel.
  const [processInput, setProcessInput] = createSignal("");
  const [resolvedPid, setResolvedPid] = createSignal<number | null>(null);
  const [byProcessError, setByProcessError] = createSignal<string | null>(null);
  const [byProcessStatus, setByProcessStatus] = createSignal<PanelStatus | null>(null);
  const [processFilter, setProcessFilter] = createSignal("");

  // Mode "byName"
  const [filename, setFilename] = createSignal("");
  const [activeFilename, setActiveFilename] = createSignal<string | null>(null);
  const [findStatus, setFindStatus] = createSignal<PanelStatus | null>(null);
  const [findFilter, setFindFilter] = createSignal("");

  // ── handles_check_available (auto, en boucle modérée) ─────────────────
  const checkQuery = createToolQuery<CheckResult>(() => ({
    tool: "handles_check_available",
    params: {},
    enabled: enabled() && !!pluginVersion(),
    refetchInterval: 0,
  }));

  // ── handles_list_by_process (déclenché par bouton via setResolvedPid) ──
  const listQuery = createToolQuery<ListByProcessResult>(() => ({
    tool: "handles_list_by_process",
    params: { pid: resolvedPid() ?? 0 },
    enabled: enabled() && resolvedPid() != null,
    refetchInterval: 0,
  }));

  // ── handles_find_by_name (déclenché par bouton via setActiveFilename) ──
  const findQuery = createToolQuery<FindByNameResult>(() => ({
    tool: "handles_find_by_name",
    params: { filename: activeFilename() ?? "", limit: 200 },
    enabled: enabled() && !!activeFilename(),
    refetchInterval: 0,
  }));

  // ── Lister par processus (input = PID ou nom) ─────────────────────────
  async function runListByProcess() {
    setByProcessError(null);
    setByProcessStatus(null);
    const raw = processInput().trim();
    if (!raw) {
      setByProcessError("Saisir un PID ou un nom de processus.");
      return;
    }
    // Si numérique → on l'utilise directement.
    if (/^\d+$/.test(raw)) {
      const pid = Number(raw);
      if (!Number.isFinite(pid) || pid < 1) {
        setByProcessError("PID invalide.");
        return;
      }
      setResolvedPid(pid);
      return;
    }
    // Sinon → résoudre via process_list
    setByProcessStatus({ text: `Recherche du processus « ${raw} »...`, level: "info" });
    try {
      const r = await tools.call<{ rows?: { pid: number; name: string }[] }>("process_list", {
        limit: 500,
      });
      const target = raw.toLowerCase().replace(/\.exe$/, "");
      const matches = (r?.rows ?? []).filter((p) => (p.name ?? "").toLowerCase().replace(/\.exe$/, "") === target);
      if (matches.length === 0) {
        setByProcessStatus(null);
        setByProcessError(`Aucun processus nommé « ${raw} » trouvé.`);
        setResolvedPid(null);
        return;
      }
      if (matches.length > 1) {
        setByProcessStatus({
          text: `${matches.length} processus « ${raw} » trouvés — utilisation du premier (PID ${matches[0].pid}). Spécifiez un PID pour cibler.`,
          level: "warn",
        });
      } else {
        setByProcessStatus({
          text: `Processus « ${raw} » → PID ${matches[0].pid}.`,
          level: "info",
        });
      }
      setResolvedPid(matches[0].pid);
    } catch (err) {
      setByProcessStatus(null);
      setByProcessError(`Erreur résolution: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  // ── Chercher par nom de fichier ───────────────────────────────────────
  function runFindByName() {
    setFindStatus(null);
    const raw = filename().trim();
    if (!raw) {
      setFindStatus({ text: "Saisir un chemin ou un nom de fichier.", level: "warn" });
      setActiveFilename(null);
      return;
    }
    if (raw.includes("*") || raw.includes("?")) {
      setFindStatus({ text: "Globbing non autorisé (pas de * ni ?).", level: "danger" });
      setActiveFilename(null);
      return;
    }
    setActiveFilename(raw);
  }

  // ── Filtrage client-side ──────────────────────────────────────────────
  const processRows = createMemo<ProcessHandleRow[]>(() => {
    const all = listQuery.data?.handles ?? [];
    const q = processFilter().trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (h) =>
        (h.type ?? "").toLowerCase().includes(q) ||
        (h.name ?? "").toLowerCase().includes(q) ||
        (h.id ?? "").toLowerCase().includes(q),
    );
  });

  const findRows = createMemo<FindRow[]>(() => {
    const all = findQuery.data?.rows ?? [];
    const q = findFilter().trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (r) =>
        (r.process ?? "").toLowerCase().includes(q) ||
        (r.type ?? "").toLowerCase().includes(q) ||
        (r.name ?? "").toLowerCase().includes(q) ||
        String(r.pid).includes(q),
    );
  });

  // ── Colonnes ──────────────────────────────────────────────────────────
  const processColumns: Column<ProcessHandleRow>[] = [
    {
      accessorKey: "type",
      header: "Type",
      cell: (info) => (
        <span style={{ "font-size": "10px" }}>
          <PanelBadge>{String(info.getValue() ?? "")}</PanelBadge>
        </span>
      ),
    },
    {
      accessorKey: "name",
      header: "Nom",
      cell: (info) => {
        const v = String(info.getValue() ?? "");
        return (
          <span title={v} style={{ "font-family": "ui-monospace,monospace", "font-size": "11px" }}>
            {v || "—"}
          </span>
        );
      },
    },
    {
      accessorKey: "id",
      header: "Handle",
      cell: (info) => <code style={{ "font-size": "10px", opacity: ".7" }}>0x{String(info.getValue() ?? "")}</code>,
    },
  ];

  const findColumns: Column<FindRow>[] = [
    {
      accessorKey: "pid",
      header: "PID",
      cell: (info) => <code style={{ "font-size": "11px" }}>{String(info.getValue() ?? "")}</code>,
    },
    { accessorKey: "process", header: "Process" },
    {
      accessorKey: "type",
      header: "Type",
      cell: (info) => (
        <span style={{ "font-size": "10px" }}>
          <PanelBadge>{String(info.getValue() ?? "")}</PanelBadge>
        </span>
      ),
    },
    {
      accessorKey: "name",
      header: "Path",
      cell: (info) => {
        const v = String(info.getValue() ?? "");
        return (
          <span title={v} style={{ "font-family": "ui-monospace,monospace", "font-size": "11px" }}>
            {v || "—"}
          </span>
        );
      },
    },
  ];

  // ── Statut global pour le PanelLayout ─────────────────────────────────
  const status = (): PanelStatus | undefined => {
    if (!ws.state.nativeAgentOnline) return { text: "Module natif requis.", level: "warn" };
    if (!ws.state.supportApproved) return { text: "Validation client requise.", level: "warn" };
    if (!pluginVersion())
      return {
        text: "Plugin Handles non chargé sur ce host.",
        level: "warn",
      };
    if (checkQuery.isFetching && !checkQuery.data) return { text: "Vérification de handle.exe...", level: "info" };
    const c = checkQuery.data;
    if (c && c.available === false)
      return {
        text: "handle.exe (Sysinternals) introuvable sur le host — voir CTA d'installation ci-dessous.",
        level: "warn",
      };

    if (mode() === "byProcess") {
      if (byProcessError()) return { text: byProcessError()!, level: "danger" };
      if (listQuery.isFetching) return { text: "Listing handles...", level: "info" };
      if (listQuery.error)
        return {
          text: `Erreur: ${listQuery.error instanceof Error ? listQuery.error.message : String(listQuery.error)}`,
          level: "danger",
        };
      return byProcessStatus() ?? undefined;
    } else {
      if (findQuery.isFetching) return { text: "Recherche en cours...", level: "info" };
      if (findQuery.error)
        return {
          text: `Erreur: ${findQuery.error instanceof Error ? findQuery.error.message : String(findQuery.error)}`,
          level: "danger",
        };
      return findStatus() ?? undefined;
    }
  };

  // ── Toolbar (mode switch) ─────────────────────────────────────────────
  const actions = (
    <>
      <button
        type="button"
        class={`btn btn--xs ${mode() === "byProcess" ? "btn--info" : "btn--ghost"}`}
        onClick={() => setMode("byProcess")}
      >
        Par processus
      </button>
      <button
        type="button"
        class={`btn btn--xs ${mode() === "byName" ? "btn--info" : "btn--ghost"}`}
        onClick={() => setMode("byName")}
      >
        Rechercher handle
      </button>
      <Show when={pluginVersion()}>
        {(v) => <span style={{ "font-size": "10px", opacity: ".5", "margin-left": "0.5rem" }}>plugin v{v()}</span>}
      </Show>
      <Show when={checkQuery.data?.available && checkQuery.data?.version}>
        <span style={{ "font-size": "10px", opacity: ".5" }}>handle.exe v{checkQuery.data!.version}</span>
      </Show>
    </>
  );

  // ── CTA d'installation si handle.exe absent ───────────────────────────
  const installCta = () => {
    const c = checkQuery.data;
    if (!c || c.available !== false) return null;
    return (
      <div
        style={{
          padding: "1rem",
          margin: "0.5rem 0",
          border: "1px solid #5a4a1a",
          background: "#2a2418",
          "border-radius": "6px",
          "font-size": "12px",
        }}
      >
        <div style={{ "font-weight": "600", "margin-bottom": "0.5rem" }}>handle.exe (Sysinternals) requis</div>
        <div style={{ opacity: ".8", "margin-bottom": "0.5rem" }}>
          {c.note ?? "Installer Sysinternals Handle et placer handle.exe dans le PATH du host."}
        </div>
        <a
          href="https://learn.microsoft.com/sysinternals/downloads/handle"
          target="_blank"
          rel="noopener noreferrer"
          class="btn btn--xs btn--info"
        >
          Télécharger Handle
        </a>
      </div>
    );
  };

  return (
    <PanelChrome icon="🔗" title="Handles ouverts" tagType="core" actions={actions}>
      <PanelLayout title="Handles ouverts" status={status()}>
        {installCta()}

        <Show when={mode() === "byProcess"}>
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              "align-items": "center",
              padding: "0.5rem 0",
              "flex-wrap": "wrap",
            }}
          >
            <input
              type="text"
              class="panel-filter-input"
              placeholder="PID (ex: 1234) ou nom de process (ex: explorer)"
              spellcheck={false}
              value={processInput()}
              onInput={(e) => setProcessInput(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") runListByProcess();
              }}
              style={{ "min-width": "300px" }}
            />
            <button
              type="button"
              class="btn btn--xs btn--info"
              onClick={runListByProcess}
              disabled={!enabled() || !checkQuery.data?.available}
            >
              Lister
            </button>
            <Show when={resolvedPid()}>
              <button type="button" class="btn btn--xs btn--ghost" onClick={() => listQuery.refetch()}>
                ↺
              </button>
            </Show>
            <Show when={listQuery.data}>
              <input
                type="text"
                class="panel-filter-input"
                placeholder="Filtrer (type, nom, handle)..."
                spellcheck={false}
                value={processFilter()}
                onInput={(e) => setProcessFilter(e.currentTarget.value)}
                style={{ "min-width": "200px" }}
              />
              <span style={{ "font-size": "11px", opacity: ".6" }}>
                {processRows().length} / {listQuery.data!.count} handles
              </span>
            </Show>
          </div>

          <Show
            when={resolvedPid() != null}
            fallback={
              <div class="panel-data-empty" style={{ opacity: ".5", padding: "1rem" }}>
                Saisir un PID ou un nom de processus puis cliquer sur Lister.
              </div>
            }
          >
            <DataTable<ProcessHandleRow>
              rows={processRows()}
              columns={processColumns}
              emptyMessage={listQuery.isFetching ? "Chargement..." : "Aucun handle."}
            />
          </Show>
        </Show>

        <Show when={mode() === "byName"}>
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              "align-items": "center",
              padding: "0.5rem 0",
              "flex-wrap": "wrap",
            }}
          >
            <input
              type="text"
              class="panel-filter-input"
              placeholder="Pattern fichier (ex: C:\Users\moi\rapport.docx)"
              spellcheck={false}
              value={filename()}
              onInput={(e) => setFilename(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") runFindByName();
              }}
              style={{ "min-width": "360px" }}
            />
            <button
              type="button"
              class="btn btn--xs btn--info"
              onClick={runFindByName}
              disabled={!enabled() || !checkQuery.data?.available}
            >
              Chercher
            </button>
            <Show when={activeFilename()}>
              <button type="button" class="btn btn--xs btn--ghost" onClick={() => findQuery.refetch()}>
                ↺
              </button>
            </Show>
            <Show when={findQuery.data}>
              <input
                type="text"
                class="panel-filter-input"
                placeholder="Filtrer (process, pid, path)..."
                spellcheck={false}
                value={findFilter()}
                onInput={(e) => setFindFilter(e.currentTarget.value)}
                style={{ "min-width": "200px" }}
              />
              <span style={{ "font-size": "11px", opacity: ".6" }}>
                {findRows().length} / {findQuery.data!.count} résultats
              </span>
            </Show>
          </div>

          <Show
            when={activeFilename()}
            fallback={
              <div class="panel-data-empty" style={{ opacity: ".5", padding: "1rem" }}>
                Saisir un nom ou chemin de fichier puis cliquer sur Chercher pour identifier les processus qui tiennent
                ce handle.
              </div>
            }
          >
            <DataTable<FindRow>
              rows={findRows()}
              columns={findColumns}
              emptyMessage={
                findQuery.isFetching ? "Recherche..." : "Aucun processus ne tient ce handle (ou nom introuvable)."
              }
            />
          </Show>
        </Show>
      </PanelLayout>
    </PanelChrome>
  );
};

registerPanel("handles", HandlesPanel);
export default HandlesPanel;
